import { App, AppStatus, CustomDomainStatus, DeploymentStatus, TaskStatus } from '@back4app2/sdk';

export type AppStatusTone = 'ok' | 'warn' | 'error' | 'info' | 'idle';

export interface AppStatusSummary {
  /** The word shown to the customer. */
  text: string;
  tone: AppStatusTone;
  /** True while something is actively happening, so callers can spin. */
  isBusy: boolean;
}

/**
 * The single reading of "what state is this app in".
 *
 * Extracted from `MyAppStatus` so the overview's STATUS card and the status
 * line in the sidebar cannot drift apart — they were one component before and
 * the design now needs the same answer in two shapes.
 */
export const getAppStatusSummary = (app: App): AppStatusSummary => {
  const env = app.mainService && app.mainService.mainServiceEnvironment;
  const lastDeployment = env && env.lastDeployment;
  const deployTaskStatus = lastDeployment && lastDeployment.deployTask && lastDeployment.deployTask.status;

  // App state outranks every deployment state below, including the absence of
  // one: a blocked app and an app waiting on the pre-deploy screen have no
  // deployment, and both used to fall through to "Initialized".
  if (app.block) {
    return { text: 'Blocked', tone: 'error', isBusy: false };
  }
  if (app.pendingFirstBuild) {
    return { text: 'Needs setup', tone: 'warn', isBusy: false };
  }
  if (app.status === AppStatus.INITIALIZING) {
    return { text: 'Initializing', tone: 'info', isBusy: true };
  }
  if (app.status === AppStatus.PENDING_VERIFICATION) {
    return { text: 'Pending Verification', tone: 'warn', isBusy: false };
  }

  if (app.status === AppStatus.INITIALIZED && env) {
    const isPendingPayment = env.isPendingPayment
      || (!env.isFreePlanElegible && env.plan.name.includes('Free'));

    if (isPendingPayment) {
      return { text: 'Pending Payment', tone: 'warn', isBusy: false };
    }
    if (env.activeDeployment) {
      return { text: 'Available', tone: 'ok', isBusy: false };
    }
    if (lastDeployment && lastDeployment.status) {
      const isInFlight = [
        DeploymentStatus.INITIALIZING,
        DeploymentStatus.INITIALIZED,
        DeploymentStatus.DEPLOYING,
        DeploymentStatus.QUEUED,
      ].includes(lastDeployment.status);

      if (isInFlight && deployTaskStatus !== TaskStatus.FAILED) {
        return { text: 'Deploying', tone: 'info', isBusy: true };
      }
      if (env.mainCustomDomain.status === CustomDomainStatus.EXPIRED) {
        return { text: 'URL Expired', tone: 'warn', isBusy: false };
      }
      if (env.mainCustomDomain.status === CustomDomainStatus.EXPIRING) {
        return { text: 'URL Expiring', tone: 'warn', isBusy: false };
      }
      return { text: 'Unavailable', tone: 'error', isBusy: false };
    }
    return { text: 'Initialized', tone: 'info', isBusy: true };
  }

  return { text: 'Failed', tone: 'error', isBusy: false };
};
